import { useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query"
import { api } from "../lib/api"
import type { FormConfig } from "../types/form-types"
import { logger } from "@/utils/logger"

export default function useForms(mode: string) {
	api.interceptors.request.use(config => {
		config.headers["mode"] = mode
		return config
	})

	const useGetFormById = (
		universityId: string,
		batchId: string,
		formId: string,
	) => {
		return useSuspenseQuery({
			queryFn: async () => {
				const { data } = await api.get<FormConfig>(
					`/universities/${universityId}/batches/${batchId}/forms/${formId}`,
				)
				return data
			},
			queryKey: ["form", universityId, batchId, formId],
		})
	}

	const useGetApplicationStageSubmissionById = (
		universityId: string,
		batchId: string,
		applicationId: string,
		stageId: string,
		formId: string,
	) => {
		return useQuery({
			queryFn: async () => {
				try {
					const { data } = await api.get<{
						data: Record<string, any>
						verifications: string[]
					}>(
						`universities/${universityId}/batches/${batchId}/applications/${applicationId}/stages/${stageId}?form_id=${formId}`,
					)
					return data
				} catch (err) {
					logger.error("error fetching submission details: ", err)
				}
			},
			queryKey: [
				"get-submission",
				universityId,
				batchId,
				applicationId,
				stageId,
				formId,
			],
			refetchOnWindowFocus: false,
			enabled:
				!!applicationId &&
				!!batchId &&
				!!formId &&
				!!stageId &&
				!!universityId,
		})
	}

	const useSubmitFormStage = (
		universityId: string,
		batchId: string,
		applicationId: string,
		stageId: string,
		leadId: string,
		formId: string,
	) => {
		return useMutation({
			mutationFn: async (payload: Record<string, unknown>) => {
				const formData = new FormData()

				for (const [key, value] of Object.entries(payload)) {
					if (value !== null && value !== undefined) {
						if (Array.isArray(value) && value[0] instanceof File) {
							// Handle multiple files
							for (const file of value as File[]) {
								formData.append(key, file)
							}
						} else if (value instanceof File) {
							// Handle single file
							formData.append(key, value)
						} else if (value instanceof Date) {
							// Handle Date object
							formData.append(key, value.toISOString())
						} else if (typeof value === "object") {
							formData.append(key, JSON.stringify(value))
						} else {
							// Handle strings, numbers, booleans
							formData.append(key, `${value}`)
						}
					}
				}

				const { data } = await api.post(
					`universities/${universityId}/batches/${batchId}/applications/${applicationId}/stages/${stageId}/submit?lead_id=${leadId}&form_id=${formId}`,
					formData,
					{
						headers: {
							"Content-Type": "multipart/form-data",
						},
					},
				)

				return data
			},

			mutationKey: [
				"submit-form-stage",
				universityId,
				batchId,
				applicationId,
				stageId,
			],
		})
	}

	const useGetApplicationSubmission = (
		universityId: string,
		batchId: string,
		applicationId: string,
	) => {
		return useQuery({
			queryFn: async () => {
				const { data } = await api.get(
					`universities/${universityId}/batches/${batchId}/applications/${applicationId}/stages`,
				)
				return data
			},
			queryKey: [
				"get-application-submission",
				universityId,
				batchId,
				applicationId,
			],
			enabled: !!applicationId && !!batchId,
			refetchOnWindowFocus: false,
		})
	}

	return {
		useGetFormById,
		useGetApplicationStageSubmissionById,
		useSubmitFormStage,
		useGetApplicationSubmission,
	}
}
